import type {
  KnowledgeGraphEdgeViewModel,
  KnowledgeGraphNodeStatus,
  KnowledgeGraphNodeViewModel,
} from '../types/learningAnalysis';

export type KnowledgeGraphLayoutPoint = {
  x: number;
  y: number;
};

export type KnowledgeGraphLayoutNode = KnowledgeGraphNodeViewModel & {
  x: number;
  y: number;
  radius: number;
  status: KnowledgeGraphNodeStatus;
};

export type KnowledgeGraphEdgeLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export const KNOWLEDGE_GRAPH_CANVAS_WIDTH = 320;
export const KNOWLEDGE_GRAPH_CANVAS_HEIGHT = 230;
export const DEFAULT_KNOWLEDGE_GRAPH_RADIUS = 40;
export const COMPACT_KNOWLEDGE_GRAPH_RADIUS = 34;

export const DEFAULT_KNOWLEDGE_GRAPH_LAYOUT: KnowledgeGraphLayoutPoint[] = [
  { x: 160, y: 54 },
  { x: 78, y: 126 },
  { x: 242, y: 126 },
  { x: 118, y: 190 },
  { x: 214, y: 190 },
  { x: 48, y: 52 },
  { x: 272, y: 52 },
];

export const getKnowledgeGraphNodeStatus = (
  node: KnowledgeGraphNodeViewModel
): KnowledgeGraphNodeStatus => {
  if (node.status) {
    return node.status;
  }
  if (node.masteryPercent >= 80) {
    return 'mastered';
  }
  if (node.masteryPercent >= 60) {
    return 'review';
  }
  return 'weak';
};

export const resolveKnowledgeGraphLayout = (
  nodes: KnowledgeGraphNodeViewModel[],
  compact = false
): KnowledgeGraphLayoutNode[] =>
  nodes.map((node, index) => {
    const position = DEFAULT_KNOWLEDGE_GRAPH_LAYOUT[index % DEFAULT_KNOWLEDGE_GRAPH_LAYOUT.length];
    return {
      ...node,
      x: node.x ?? position.x,
      y: node.y ?? position.y,
      radius: compact ? COMPACT_KNOWLEDGE_GRAPH_RADIUS : DEFAULT_KNOWLEDGE_GRAPH_RADIUS,
      status: getKnowledgeGraphNodeStatus(node),
    };
  });

export const createKnowledgeGraphEdges = (
  nodes: KnowledgeGraphNodeViewModel[]
): KnowledgeGraphEdgeViewModel[] => {
  if (nodes.length < 2) {
    return [];
  }

  const [center, ...children] = nodes;
  return children.map((node) => ({
    id: `${center.id}-${node.id}`,
    sourceId: center.id,
    targetId: node.id,
    strengthLabel: '关联',
  }));
};

export const resolveKnowledgeGraphEdgeLine = (
  source: Pick<KnowledgeGraphLayoutNode, 'x' | 'y' | 'radius'>,
  target: Pick<KnowledgeGraphLayoutNode, 'x' | 'y' | 'radius'>
): KnowledgeGraphEdgeLine => {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance === 0) {
    return {
      x1: source.x,
      y1: source.y,
      x2: target.x,
      y2: target.y,
    };
  }

  const unitX = dx / distance;
  const unitY = dy / distance;
  const sourceOffset = Math.min(source.radius, distance / 2);
  const targetOffset = Math.min(target.radius, distance / 2);

  return {
    x1: source.x + unitX * sourceOffset,
    y1: source.y + unitY * sourceOffset,
    x2: target.x - unitX * targetOffset,
    y2: target.y - unitY * targetOffset,
  };
};
